import { useState, useMemo } from 'react';
import { useMutation, useQuery } from 'react-apollo';
import { useSelector } from 'react-redux';
import produce from 'immer';
import { defaultDataIdFromObject } from 'apollo-cache-inmemory';

import {
  AlbumPhotosForSearch,
  AlbumPhotosWithPhotoGroups,
  AlbumSlideshow as AlbumSlideshowQuery,
  AlbumSlideshowForContributors as AlbumSlideshowForContributorsQuery,
} from '../../../../../../graphql/queries/albums';
import {
  albumSlideshowUpdate as albumSlideshowUpdateMutation,
  albumSlideshowContributeSend as albumSlideshowContributeSendMutation,
} from '../../../../../../graphql/mutations/albums';
import { createLoadMoreMethod } from '../../../../../../util/apollo';
import { getErrorString } from '../../../../../../util/normalize-errors';
import useOpenAlert from '../../../../../../hooks/useOpenAlert';
import { useContributorPermission } from '../../../../services/contributors';
import {
  ALBUM_PHOTO_FILTERS,
  ALBUM_SLIDESHOW_CURATE_ACTIONS,
  ALBUM_SLIDESHOW_MODE,
  ALBUM_SLIDESHOW_PHOTOS_SOURCE,
} from '../../../../../../constants/albums';
import { ALBUM_FOLDER_TAG_SEARCH_FILTER_TYPE } from '../../../../../../constants/album-folder-tags';
import { CONTRIBUTOR_UPLOAD_ACCESS } from '../../../../../../constants/contributors';
import { SlideshowCurationPhotoDetails } from '../../../../../../graphql/fragments/album';
import {
  isAllPhotosSlideshow,
  isAutoLayoutsSlideshow,
  isSelectedPhotosSlideshow,
} from '../../../../../../util/album';

/**
 * Album Slideshow hook.
 *
 * @param {Object} options The options.
 * @param {String} [options.albumId] The album id
 * @param {Number} [options.limit] the amount of items to fetch for slideshow photos & layouts
 * @param {Object} [options.queryProps] props to apss down to the fetch queries
 */
const useAlbumSlideshow = ({ albumId, limit = 10, queryProps = {} }) => {
  const openAlert = useOpenAlert();
  const [saving, setSaving] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const authenticated = useSelector(({ auth }) => auth.authenticated);
  const { isOwner, isContributor } = useContributorPermission({
    entityId: albumId,
  });

  const { fetchMore, loading, data: { album } = {}, refetch } = useQuery(
    AlbumSlideshowQuery,
    {
      variables: { id: albumId, limit },
      fetchPolicy: 'cache-and-network',
      skip: !albumId || isContributor || !authenticated,
      ...queryProps,
    },
  );

  const {
    fetchMore: contributorFetchMore,
    loading: contributorLoading,
    data: { album: contributorAlbum } = {},
  } = useQuery(AlbumSlideshowForContributorsQuery, {
    variables: { id: albumId, limit },
    fetchPolicy: 'cache-and-network',
    skip: !albumId || (!isContributor && authenticated),
    ...queryProps,
  });

  const isAutoLayout = isAutoLayoutsSlideshow(
    (album ?? contributorAlbum)?.slideshow,
  );

  const loadMore = useMemo(
    () =>
      createLoadMoreMethod({
        source: album ?? contributorAlbum,
        recordsPath: isAutoLayout ? 'slideshow.layouts' : 'slideshow.photos',
        fetchMore: album ? fetchMore : contributorFetchMore,
        limit: 10,
      }),
    [album, contributorAlbum, isAutoLayout],
  );

  const onLoadMore = async (props = {}) => {
    if (loadingMore) {
      return;
    }

    setLoadingMore(true);

    try {
      await loadMore(props);
    } finally {
      setLoadingMore(false);
    }
  };

  const [albumSlideshowUpdate] = useMutation(albumSlideshowUpdateMutation);
  const [albumSlideshowContributeSend] = useMutation(
    albumSlideshowContributeSendMutation,
  );

  const onSlideshowUpdate = async ({
    status,
    mode,
    reviewPhotos,
    slideDurationMs,
    slideBackgroundColor,
    slideBackgroundAssetImage,
    createExcludedPhotos,
    deleteExcludedPhotos,
    createSelectedPhotos,
    createSelectedPhotosFromSource,
    createSelectedPhotoLayouts,
    deleteSelectedPhotos,
    introAssetImage,
    qrCode,
    ticker,
    createPhotos,
    updateLayouts,
    createLayouts,
    deleteLayouts,
    createAutoLayouts,
    deleteAllLayouts,
    queryOptions,
    introSlideText,
    introSlideTextColor,
    introSlideBackgroundColor,
    introSlideLabelEnabled,
    albumInput,
  }) => {
    let result = null;
    if (saving) {
      return result;
    }

    setSaving(true);
    try {
      result = await albumSlideshowUpdate({
        variables: {
          input: {
            id: albumId,
            member_uploads_require_review:
              typeof reviewPhotos !== 'undefined' ? reviewPhotos : undefined,
            external_uploads_require_review:
              typeof reviewPhotos !== 'undefined' ? reviewPhotos : undefined,
            auto_contributor_settings:
              typeof reviewPhotos !== 'undefined'
                ? {
                    view_access: album?.auto_contributor_settings?.view_access,
                    upload_access: reviewPhotos
                      ? CONTRIBUTOR_UPLOAD_ACCESS.REVIEW
                      : CONTRIBUTOR_UPLOAD_ACCESS.PUBLISH,
                  }
                : undefined,
            slideshow: {
              status,
              mode,
              slide_duration_ms: slideDurationMs,
              slide_background_color: slideBackgroundColor,
              slide_background_asset_image: slideBackgroundAssetImage,
              create_excluded_photos: createExcludedPhotos,
              delete_excluded_photos: deleteExcludedPhotos,
              create_selected_photos: createSelectedPhotos,
              create_selected_photos_from_source: createSelectedPhotosFromSource,
              create_selected_photo_layouts: createSelectedPhotoLayouts,
              delete_selected_photos: deleteSelectedPhotos,
              intro_asset_image: introAssetImage,
              qr_code: qrCode,
              create_photos: createPhotos,
              update_layouts: updateLayouts,
              create_layouts: createLayouts,
              delete_layouts: deleteLayouts,
              create_auto_layouts: createAutoLayouts,
              delete_all_layouts: deleteAllLayouts,
              intro_slide_text: introSlideText,
              intro_slide_text_color: introSlideTextColor,
              intro_slide_background_color: introSlideBackgroundColor,
              intro_slide_label_enabled: introSlideLabelEnabled,
              ticker,
            },
            ...(albumInput ?? {}),
          },
        },
        update: (store, { data: { albumUpdate: response } }) => {
          const {
            auto_contributor_settings: autoContribSettings,
            slideshow: updatedSlideshow,
          } = response.album;

          try {
            const data = store.readQuery({
              query: AlbumSlideshowQuery,
              variables: { id: albumId },
            });

            const updatedData = produce(data, draft => {
              draft.album = {
                ...draft.album,
                auto_contributor_settings: autoContribSettings,
                slideshow: {
                  ...draft.album.slideshow,
                  ...updatedSlideshow,
                },
              };
            });

            store.writeQuery({
              query: AlbumSlideshowQuery,
              variables: { id: albumId },
              data: updatedData,
            });
          } catch {
            // Nothing to do here. Queries might not be loaded.
          }

          try {
            const data = store.readQuery({
              query: AlbumSlideshowForContributorsQuery,
              variables: { id: albumId },
            });

            // Omit selected & excluded for Contributors
            const {
              selected_photos,
              excluded_photos,
              ...contributorUpdatedSlideshow
            } = updatedSlideshow;

            const updatedData = produce(data, draft => {
              draft.album = {
                ...draft.album,
                auto_contributor_settings: autoContribSettings,
                slideshow: {
                  ...draft.album.slideshow,
                  ...contributorUpdatedSlideshow,
                },
              };
            });

            store.writeQuery({
              query: AlbumSlideshowForContributorsQuery,
              variables: { id: albumId },
              data: updatedData,
            });
          } catch {
            // Nothing to do here. Queries might not be loaded.
          }
        },
        ...(queryOptions ?? {}),
      });
    } catch (err) {
      openAlert({
        message: 'There was an error updating the slideshow.',
        info: getErrorString(err),
      });
    } finally {
      setSaving(false);
    }

    return result;
  };

  const onSlideshowDelete = async () => {
    if (saving) {
      return;
    }

    setSaving(true);

    try {
      await albumSlideshowUpdate({
        variables: {
          input: {
            id: albumId,
            slideshow: null,
          },
        },
      });
    } catch (err) {
      openAlert({
        message: 'There was an error updating the slideshow.',
        info: getErrorString(err),
      });
    } finally {
      setSaving(false);
    }
  };

  const isInSlideshow = photo => {
    const slideshow = album?.slideshow;
    if (!slideshow) {
      return false;
    }

    return slideshow.mode === ALBUM_SLIDESHOW_MODE.SELECTED_PHOTOS
      ? slideshow.selected_photos.records.find(
          slideshowPhoto => slideshowPhoto.photo.id === photo.id,
        )
      : !slideshow.excluded_photos.records.find(
          slideshowPhoto => slideshowPhoto.photo.id === photo.id,
        );
  };

  const onSlideshowToggle = async photo => {
    try {
      // Do not care for Contributor. This since they don't have access
      // to selected nor excluded photos, so they would not be accessing this.
      const slideshow = album?.slideshow;
      const inSlideshow = photo.is_album_slideshow_photo;

      const isAllPhotos = isAllPhotosSlideshow(slideshow);
      const isSelectedPhotos = isSelectedPhotosSlideshow(slideshow);

      const createExcludedPhotos =
        inSlideshow && isAllPhotos ? [{ photo_id: photo.id }] : [];
      const deleteExcludedPhotos =
        !inSlideshow && isAllPhotos ? [{ photo_id: photo.id }] : [];
      const createSelectedPhotos =
        !inSlideshow && isSelectedPhotos ? [{ photo_id: photo.id }] : [];
      const deleteSelectedPhotos =
        inSlideshow && isSelectedPhotos ? [{ photo_id: photo.id }] : [];
      await onSlideshowUpdate({
        createExcludedPhotos,
        deleteExcludedPhotos,
        createSelectedPhotos,
        deleteSelectedPhotos,
        queryOptions: {
          update: store => {
            // Try updating the queries where the photo would appear.
            // These are basically two queries: AlbumPhotosForSearch &
            // AlbumPhotosWithPhotoGroups
            try {
              const data = store.readQuery({
                query: AlbumPhotosForSearch,
                variables: {
                  id: albumId,
                  limit: 25,
                  offset: 0,
                  tagIds: [],
                  filterMode: ALBUM_FOLDER_TAG_SEARCH_FILTER_TYPE.ANY,
                },
              });

              const updatedData = produce(data, draft => {
                draft.album = {
                  ...draft.album,
                  photos: {
                    ...draft.album.photos,
                    records: draft.album.photos.records.map(p => {
                      if (p.id !== photo.id) {
                        return p;
                      }

                      return {
                        ...p,
                        is_album_slideshow_photo: !inSlideshow,
                      };
                    }),
                  },
                };
              });

              store.writeQuery({
                query: AlbumPhotosForSearch,
                variables: {
                  id: albumId,
                  limit: 25,
                  offset: 0,
                  tagIds: [],
                  filterMode: ALBUM_FOLDER_TAG_SEARCH_FILTER_TYPE.ANY,
                },
                data: updatedData,
              });
            } catch {
              //  Nothing to do here, query might not be loaded
            }

            try {
              const data = store.readQuery({
                query: AlbumPhotosWithPhotoGroups,
                variables: {
                  id: albumId,
                  ignore_watermark: true,
                  limit: 100,
                  offset: 0,
                  album_photo_filter: ALBUM_PHOTO_FILTERS.PUBLISHED,
                  uploader_ids: [],
                },
              });

              const updatedData = produce(data, draft => {
                draft.album = {
                  ...draft.album,
                  photos_ungrouped: {
                    ...draft.album.photos_ungrouped,
                    records: draft.album.photos_ungrouped.records.map(p => {
                      if (p.id !== photo.id) {
                        return p;
                      }

                      return {
                        ...p,
                        is_album_slideshow_photo: !inSlideshow,
                      };
                    }),
                  },
                };
              });

              store.writeQuery({
                query: AlbumPhotosWithPhotoGroups,
                variables: {
                  id: albumId,
                  ignore_watermark: true,
                  limit: 100,
                  offset: 0,
                  album_photo_filter: ALBUM_PHOTO_FILTERS.PUBLISHED,
                  uploader_ids: [],
                },
                data: updatedData,
              });
            } catch {
              //  Nothing to do here, query might not be loaded
            }

            try {
              const photoItem = {
                __typename: 'Photo',
                id: photo.id,
              };
              const slideshowCurationPhotoDetailsFragment = store.readFragment({
                id: defaultDataIdFromObject(photoItem),
                fragment: SlideshowCurationPhotoDetails,
                fragmentName: 'SlideshowCurationPhotoDetails',
                variables: {
                  id: albumId,
                },
              });

              slideshowCurationPhotoDetailsFragment.is_album_slideshow_photo = !inSlideshow;
              store.writeFragment({
                id: defaultDataIdFromObject(photoItem),
                fragment: SlideshowCurationPhotoDetails,
                fragmentName: 'SlideshowCurationPhotoDetails',
                variables: {
                  id: albumId,
                },
                data: slideshowCurationPhotoDetailsFragment,
              });
            } catch {
              //   Nothing to do here, fragment might not be loaded
            }
          },
        },
      });
    } catch {
      // This should not really happen ever, but if it does, ignore it.
    }
  };

  const onSlideshowCurate = async ({
    isSelectAll,
    selectedPhotosIds,
    photosSource,
    action,
    onSuccess,
  }) => {
    try {
      const slideshow = album?.slideshow;
      const isAutoLayouts = isAutoLayoutsSlideshow(slideshow);

      const slideshowUpdateParams = {
        createExcludedPhotos: [],
        deleteExcludedPhotos: [],
        createSelectedPhotos: [],
        deleteSelectedPhotos: [],
        createSelectedPhotosFromSource: undefined,
        createAutoLayouts: false,
        createSelectedPhotoLayouts: false,
      };
      // Do not care for Contributor. This since they don't have access
      // to selected nor excluded photos, so they would not be accessing this.
      if (action === ALBUM_SLIDESHOW_CURATE_ACTIONS.ADD) {
        if (
          isSelectAll &&
          [
            ALBUM_SLIDESHOW_MODE.ALL_PHOTOS,
            ALBUM_SLIDESHOW_MODE.AUTO_LAYOUTS,
          ].includes(slideshow?.mode)
        ) {
          slideshowUpdateParams.deleteExcludedPhotos = (
            slideshow?.excluded_photos?.records ?? []
          )
            .filter(({ photo }) => !selectedPhotosIds.includes(photo.id))
            .map(({ photo }) => ({ photo_id: photo.id }));

          if (isAutoLayouts) {
            slideshowUpdateParams.createAutoLayouts = true;
          }
        } else if (
          isSelectAll &&
          photosSource === ALBUM_SLIDESHOW_PHOTOS_SOURCE.ALBUM_PHOTOS
        ) {
          if (isAutoLayouts) {
            slideshowUpdateParams.mode = ALBUM_SLIDESHOW_MODE.AUTO_LAYOUTS;
            slideshowUpdateParams.createAutoLayouts = true;
            slideshowUpdateParams.deleteAllLayouts = true;
          } else {
            slideshowUpdateParams.mode = ALBUM_SLIDESHOW_MODE.ALL_PHOTOS;
          }
          if (selectedPhotosIds.length === 0) {
            // Cleanup
            slideshowUpdateParams.deleteExcludedPhotos = (
              slideshow?.excluded_photos?.records ?? []
            ).map(({ photo }) => ({ photo_id: photo.id }));
          } else {
            slideshowUpdateParams.createExcludedPhotos = selectedPhotosIds
              .filter(
                id =>
                  !(slideshow?.selected_photos?.records ?? []).find(
                    ({ photo }) => photo.id === id,
                  ),
              )
              .map(id => ({ photo_id: id }));
          }
        } else if (isSelectAll) {
          slideshowUpdateParams.createSelectedPhotosFromSource = photosSource;
          if (isAutoLayouts) {
            slideshowUpdateParams.createSelectedPhotoLayouts = true;
          }
        } else if (
          [
            ALBUM_SLIDESHOW_MODE.ALL_PHOTOS,
            ALBUM_SLIDESHOW_MODE.AUTO_LAYOUTS,
          ].includes(slideshow?.mode)
        ) {
          slideshowUpdateParams.deleteExcludedPhotos = (
            slideshow?.excluded_photos?.records ?? []
          )
            .filter(({ photo }) => selectedPhotosIds.includes(photo.id))
            .map(({ photo }) => ({ photo_id: photo.id }));

          if (isAutoLayouts) {
            slideshowUpdateParams.createAutoLayouts = true;
          }
        } else {
          slideshowUpdateParams.createSelectedPhotos = selectedPhotosIds.map(
            id => ({ photo_id: id }),
          );
          if (isAutoLayouts) {
            slideshowUpdateParams.createSelectedPhotoLayouts = true;
          }
        }
      } else if (action === ALBUM_SLIDESHOW_CURATE_ACTIONS.REMOVE) {
        if (
          isSelectAll &&
          (!photosSource ||
            photosSource === ALBUM_SLIDESHOW_PHOTOS_SOURCE.ALBUM_PHOTOS) &&
          [
            ALBUM_SLIDESHOW_MODE.ALL_PHOTOS,
            ALBUM_SLIDESHOW_MODE.AUTO_LAYOUTS,
          ].includes(slideshow?.mode)
        ) {
          if (isAutoLayouts) {
            slideshowUpdateParams.mode =
              ALBUM_SLIDESHOW_MODE.SELECTED_PHOTO_LAYOUTS;
            slideshowUpdateParams.createSelectedPhotoLayouts = true;
            slideshowUpdateParams.deleteAllLayouts = true;
          } else {
            slideshowUpdateParams.mode = ALBUM_SLIDESHOW_MODE.SELECTED_PHOTOS;
            slideshowUpdateParams.createAutoLayouts = true;
            slideshowUpdateParams.deleteAllLayouts = true;
          }
          slideshowUpdateParams.createSelectedPhotos = selectedPhotosIds.map(
            id => ({ photo_id: id }),
          );
        } else if (
          isSelectAll &&
          photosSource &&
          [
            ALBUM_SLIDESHOW_MODE.ALL_PHOTOS,
            ALBUM_SLIDESHOW_MODE.AUTO_LAYOUTS,
          ].includes(slideshow?.mode)
        ) {
          // The removal of the unnecessary photos will happen on a post-update process.
          slideshowUpdateParams.createSelectedPhotosFromSource = photosSource;
        } else if (isSelectAll) {
          slideshowUpdateParams.createSelectedPhotos = selectedPhotosIds.map(
            id => ({ photo_id: id }),
          );
          slideshowUpdateParams.deleteSelectedPhotos = (
            slideshow?.selected_photos?.records ?? []
          ).map(({ photo }) => ({ photo_id: photo.id }));
        } else if (
          [
            ALBUM_SLIDESHOW_MODE.ALL_PHOTOS,
            ALBUM_SLIDESHOW_MODE.AUTO_LAYOUTS,
          ].includes(slideshow?.mode)
        ) {
          slideshowUpdateParams.createExcludedPhotos = selectedPhotosIds.map(
            id => ({ photo_id: id }),
          );
        } else {
          slideshowUpdateParams.deleteSelectedPhotos = selectedPhotosIds.map(
            id => ({ photo_id: id }),
          );
        }
      }

      const {
        data: { albumUpdate: { album: updatedAlbum } = {} } = {},
      } = await onSlideshowUpdate({ ...slideshowUpdateParams });
      const updatedSlideshow = updatedAlbum?.slideshow;

      // These post operations either need to happen post because of order of factors
      // or need to happen post because they rely on the updated values.
      const slideshowPostUpdateParams = {
        deleteSelectedPhotos: [],
      };
      if (
        isSelectAll &&
        action === ALBUM_SLIDESHOW_CURATE_ACTIONS.ADD &&
        photosSource !== ALBUM_SLIDESHOW_PHOTOS_SOURCE.ALBUM_PHOTOS
      ) {
        slideshowPostUpdateParams.deleteSelectedPhotos = selectedPhotosIds
          .filter(
            id =>
              !(slideshow?.selected_photos?.records ?? []).find(
                ({ photo }) => photo.id === id,
              ),
          )
          .map(id => ({ photo_id: id }));
        if (isAutoLayoutsSlideshow(updatedSlideshow)) {
          slideshowPostUpdateParams.createSelectedPhotoLayouts = true;
        }
      }

      if (
        isSelectAll &&
        photosSource &&
        action === ALBUM_SLIDESHOW_CURATE_ACTIONS.REMOVE &&
        photosSource !== ALBUM_SLIDESHOW_PHOTOS_SOURCE.ALBUM_PHOTOS &&
        [
          ALBUM_SLIDESHOW_MODE.ALL_PHOTOS,
          ALBUM_SLIDESHOW_MODE.AUTO_LAYOUTS,
        ].includes(slideshow?.mode)
      ) {
        slideshowPostUpdateParams.deleteSelectedPhotos = (
          updatedSlideshow?.selected_photos?.records ?? []
        ).map(({ photo }) => ({ photo_id: photo.id }));
        slideshowPostUpdateParams.createExcludedPhotos = (
          updatedSlideshow?.selected_photos?.records ?? []
        )
          .filter(({ photo }) => !selectedPhotosIds.find(id => photo.id === id))
          .map(({ photo }) => ({ photo_id: photo.id }));
        if (isAutoLayoutsSlideshow(updatedSlideshow)) {
          slideshowPostUpdateParams.createLayouts = true;
        }
      }

      if (slideshowPostUpdateParams?.deleteSelectedPhotos?.length > 0) {
        await onSlideshowUpdate({ ...slideshowPostUpdateParams });
      }
      await onSuccess?.();
    } catch (e) {
      openAlert({
        message: 'There was an error updating the slideshow.',
        info: getErrorString(e),
      });
    }
  };

  const onSlideshowContributeSend = async () => {
    try {
      await albumSlideshowContributeSend({
        variables: {
          input: {
            album_id: albumId,
          },
        },
      });
    } catch {
      // We'll handle this in a fire-away & forget mode.
      // so if there's any error, ignore it.
    }
  };

  return {
    loading: loading ?? contributorLoading,
    saving,
    album: album ?? contributorAlbum,
    slideshow: album?.slideshow ?? contributorAlbum?.slideshow,
    refetchSlideshow: refetch,
    onLoadMore,
    loadingMore,
    onSlideshowUpdate,
    onSlideshowDelete,
    onSlideshowToggle,
    onSlideshowCurate,
    onSlideshowContributeSend,
    isInSlideshow,
    isContributor,
    isOwner: isOwner && authenticated,
  };
};

export default useAlbumSlideshow;
